import {createAsyncThunk} from '@reduxjs/toolkit';
import type {GraphicsReadinessResult} from '@Components/social-media/social-media.types';
import {GRAPHICS_READINESS_MAX_WAIT_MS} from '@Components/social-media-wizard/social-media-wizard.types';
import {updateGraphicsReadiness} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {LoadingStates} from '@Utils/loading.util';

const isGraphicsReadinessResult = (value: unknown): value is GraphicsReadinessResult => {
  return typeof value === 'object' && value !== null && 'ready' in value && typeof value.ready === 'boolean';
};

const isReady = (value: unknown): boolean => {
  return isGraphicsReadinessResult(value) && value.ready;
};

/**
 * Wraps a payload in the {isAvailable, data} envelope that pollingAjaxCallAsync's loop terminates on.
 * The long-poll endpoint (awaitPublishGraphicsReadiness) already returns this shape via the polling trait,
 * but the instant kickoff summary does not — so an already-ready design must be wrapped here. Returning the
 * bare summary leaves `isAvailable` undefined, and because the ready branch does no network I/O the loop
 * degrades into a microtask-only busy-wait that pins the main thread until the 90s timeout (tab freeze).
 */
const availableEnvelope = (data: unknown): {isAvailable: true; data: unknown} => {
  return {isAvailable: true, data};
};

/**
 * Drives the publish-step readiness gate with the standard long-poll client: one instant kickoff check, then
 * polling only if needed. The "preparing your design" state is dispatched solely from the kickoff, and only when
 * that instant check comes back not-ready — so an already-rendered design publishes with no loader flash.
 */
export const pollPublishGraphicsReadiness = createAsyncThunk(
  'socialMediaWizard/pollPublishGraphicsReadiness',
  async ({idPost}: {idPost: string}, {dispatch}): Promise<GraphicsReadinessResult | undefined> => {
    const result: unknown = await window.PMW.pollingAjaxCallAsync(
      async (): Promise<unknown> => {
        const initial: unknown = await window.PMW.readLocal('socialmedia/getPublishGraphicsReadiness', {idPost});
        if (!isReady(initial)) {
          dispatch(updateGraphicsReadiness(LoadingStates.LOADING));
        }
        return initial;
      },
      async (initial: unknown): Promise<unknown> => {
        return isReady(initial) ? availableEnvelope(initial) : window.PMW.readLocal('socialmedia/awaitPublishGraphicsReadiness', {idPost});
      },
      GRAPHICS_READINESS_MAX_WAIT_MS
    );

    return isGraphicsReadinessResult(result) ? result : undefined;
  }
);
